import { BaseEffect } from '../BaseEffect.js';

/**
 * God Rays effect implementation
 * Creates volumetric lighting with light scattering through a medium
 */
export class GodRaysEffect extends BaseEffect {
    constructor(shaderManager) {
        const defaultParams = {
            lightPosX: 0.5,        // Light source X position (0-1)
            lightPosY: 0.2,        // Light source Y position (0-1)
            exposure: 0.5,         // Overall brightness
            decay: 0.95,          // How quickly the light fades
            density: 0.5,         // Density of the rays
            weight: 0.3,          // Light ray weight
            samples: 100,         // Number of samples for the ray march
            opacity: 0.25,         // Effect opacity
            animate: true,        // Whether to animate the light position
            animationSpeed: 1.0,  // Speed of the animation
            orbitRadius: 0.2,     // Radius of light orbit
            pulseIntensity: 0.2   // Intensity of exposure pulsing
        };

        super('god-rays', shaderManager, defaultParams);
    }

    /**
     * Initialize the effect
     */
    async init() {
        const vertexSource = `
            precision highp float;
            
            uniform float u_time;
            uniform vec2 u_resolution;
            
            attribute vec2 a_position;
            attribute vec2 a_texCoord;
            
            varying vec2 v_texcoord;
            varying vec2 v_resolution;
            varying float v_time;
            
            void main() {
                gl_Position = vec4(a_position, 0, 1);
                v_texcoord = a_texCoord;
                v_resolution = u_resolution;
                v_time = u_time;  // Pass time to fragment shader
            }
        `;

        const fragmentSource = `
            precision mediump float;
            
            uniform sampler2D u_image;
            uniform vec2 u_lightPos;
            uniform float u_exposure;
            uniform float u_decay;
            uniform float u_density;
            uniform float u_weight;
            uniform float u_samples;
            uniform float u_opacity;
            
            varying vec2 v_texcoord;
            varying vec2 v_resolution;
            varying float v_time;
            
            void main() {
                // Calculate aspect-correct UV coordinates
                vec2 uv = v_texcoord;
                float aspect = v_resolution.x / v_resolution.y;
                
                // Adjust light position based on aspect ratio
                vec2 lightPos = u_lightPos;
                lightPos.x *= aspect;
                
                // Calculate vector from pixel to light
                vec2 deltaTexCoord = (uv - lightPos);
                deltaTexCoord.x /= aspect; // Correct for aspect ratio
                
                // Scale by density and number of samples
                deltaTexCoord *= 1.0 / u_samples * u_density;
                
                // Store initial sample
                vec4 color = texture2D(u_image, uv);
                float illuminationDecay = 1.0;
                vec2 texCoord = uv;
                
                // Evaluate samples along the ray
                for(float i = 0.0; i < 100.0; i++) {
                    if(i >= u_samples) break;
                    
                    texCoord -= deltaTexCoord;
                    vec4 sample = texture2D(u_image, texCoord);
                    sample *= illuminationDecay * u_weight;
                    color += sample;
                    illuminationDecay *= u_decay;
                }
                
                // Apply exposure
                color *= u_exposure;
                
                // Mix with original image
                vec4 original = texture2D(u_image, uv);
                gl_FragColor = mix(original, color, u_opacity);
            }
        `;

        await super.init(vertexSource, fragmentSource);
    }

    /**
     * Set up effect-specific uniforms
     */
    setupUniforms() {
        // Call base class setupUniforms first to set up common uniforms like u_time and u_resolution
        super.setupUniforms();
        
        // Add effect-specific uniforms
        this.addUniform('u_lightPos', { type: '2f' });
        this.addUniform('u_exposure', { type: '1f' });
        this.addUniform('u_decay', { type: '1f' });
        this.addUniform('u_density', { type: '1f' });
        this.addUniform('u_weight', { type: '1f' });
        this.addUniform('u_samples', { type: '1f' });
        this.addUniform('u_opacity', { type: '1f' });
        
        // Explicitly add u_time uniform in case it wasn't added by super
        this.addUniform('u_time', { type: '1f' });
    }

    /**
     * Update effect-specific uniforms
     * @param {Object} uniforms - Uniform values to update
     */
    updateUniforms(uniforms) {
        // Call parent class to handle common uniforms like u_resolution and u_time
        super.updateUniforms(uniforms);
        
        // Get base light position
        let lightX = this.params.lightPosX;
        let lightY = this.params.lightPosY;
        let currentExposure = this.params.exposure;
        let currentDensity = this.params.density;
        
        // Handle animation
        if (this.params.animate) {
            const time = uniforms.u_time ? uniforms.u_time.value : 0;
            const animTime = time * this.params.animationSpeed;
            
            // Create orbital motion
            const orbit = this.params.orbitRadius;
            lightX += Math.sin(animTime * 0.5) * orbit;
            lightY += Math.cos(animTime * 0.7) * orbit;
            
            // Add a figure-8 pattern
            lightX += Math.sin(animTime * 0.3) * orbit * 0.3;
            lightY += Math.sin(animTime * 0.6) * Math.cos(animTime * 0.3) * orbit * 0.3;
            
            // Animate exposure with pulsing
            const pulseAmount = this.params.pulseIntensity;
            currentExposure += Math.sin(animTime * 1.2) * pulseAmount;
            
            // Animate density for ray length variation
            currentDensity += Math.cos(animTime * 0.8) * 0.1;
        }

        // Clamp values to valid ranges
        currentExposure = Math.max(0.1, Math.min(1.0, currentExposure));
        currentDensity = Math.max(0.1, Math.min(1.0, currentDensity));
        lightX = Math.max(0, Math.min(1, lightX));
        lightY = Math.max(0, Math.min(1, lightY));
        
        // Handle effect-specific uniforms
        Object.assign(uniforms, {
            u_lightPos: { value: [lightX, lightY], type: '2f' },
            u_exposure: { value: currentExposure, type: '1f' },
            u_decay: { value: this.params.decay, type: '1f' },
            u_density: { value: currentDensity, type: '1f' },
            u_weight: { value: this.params.weight, type: '1f' },
            u_samples: { value: this.params.samples, type: '1f' },
            u_opacity: { value: this.params.opacity, type: '1f' }
        });

        // Update time value based on animation state
        if (!this.params.animate) {
            uniforms.u_time.value = 0;
        }
    }

    /**
     * Get UI configuration for parameters
     * @returns {Object} UI configuration
     */
    getUIConfig() {
        return {
            lightPosX: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Light X Position'
            },
            lightPosY: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Light Y Position'
            },
            exposure: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Exposure'
            },
            decay: {
                min: 0.8,
                max: 1.0,
                step: 0.01,
                label: 'Decay'
            },
            density: {
                min: 0.1,
                max: 1.0,
                step: 0.01,
                label: 'Density'
            },
            weight: {
                min: 0.01,
                max: 1.0,
                step: 0.01,
                label: 'Weight'
            },
            samples: {
                min: 20,
                max: 100,
                step: 1,
                label: 'Quality'
            },
            opacity: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Opacity'
            },
            animate: {
                label: 'Animate Light'
            },
            animationSpeed: {
                min: 0.1,
                max: 2.0,
                step: 0.1,
                label: 'Animation Speed'
            },
            orbitRadius: {
                min: 0.0,
                max: 0.4,
                step: 0.01,
                label: 'Orbit Size'
            },
            pulseIntensity: {
                min: 0.0,
                max: 0.4,
                step: 0.01,
                label: 'Pulse Intensity'
            }
        };
    }
} 